"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Building2, Crown, Vote, Shield, Briefcase, Users, Lock, ArrowRight, Crosshair, Target, Sparkles } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const TARGETS = [
  { icon: Building2, title: "Governments and Heads of State", body: "Presidential and prime ministerial offices, national governments, and sovereign institutions seeking operational excellence, governance transformation, and strategic sovereignty.", href: "/services/government/", gradient: "from-red/15", accent: "text-red" },
  { icon: Crown, title: "Royal Families and Dynasties", body: "Sovereign households requiring reputation protection, privacy enforcement, and legacy narrative management with absolute discretion. Zero security incidents across fifteen-plus years.", href: "/sectors/kingdoms-royal-families/", gradient: "from-amber-gold/15", accent: "text-amber-gold" },
  { icon: Vote, title: "Political Leaders and Organizations", body: "National and international parties, candidates, and coalitions seeking voter intelligence, campaign engineering, and verified electoral outcomes.", href: "/sectors/political-organizations/", gradient: "from-emerald/15", accent: "text-emerald" },
  { icon: Shield, title: "Defense and Intelligence Agencies", body: "Military, security, and law enforcement entities requiring secure communications, real-time threat detection, intelligence fusion, and cyber forensics.", href: "/sectors/defense-intelligence/", gradient: "from-denim/15", accent: "text-denim" },
  { icon: Briefcase, title: "Fortune-Scale Corporations", body: "Multinational enterprises seeking brand protection, crisis response, competitive positioning, and operational transformation across jurisdictions.", href: "/sectors/corporate-enterprise/", gradient: "from-purple/15", accent: "text-purple" },
  { icon: Users, title: "High-Net-Worth Individuals", body: "Prominent individuals requiring reputation protection, privacy enforcement, and strategic management of public presence. Sovereign-grade protection for the most sensitive personal information.", href: "/sectors/public-figures-hnwis/", gradient: "from-coral/15", accent: "text-coral" },
  { icon: Lock, title: "International Organizations", body: "Multilateral bodies and global institutions seeking governance modernization, citizen engagement, and policy impact assessment. With one hundred twenty-seven governance modules and one hundred twenty-nine capabilities.", href: "/sectors/international-organizations/", gradient: "from-cyan/15", accent: "text-cyan" },
];

export function IdealTargetSection() {
  return (
    <Section id="ideal-target" ariaLabel="Ideal Target" background="default">
      <SectionHeader
        icon={Crosshair}
        overline="Ideal Target"
        overlineClassName="text-cyan"
        pillClassName="glass-tinted-cyan border-cyan/20"
        title={
          <>
            Who Benefits from <span className="text-gradient-clarity">Engaging</span>
          </>
        }
        subtitle="The discretion that defines this work means clients are described by category, not by name. National security level confidentiality is non-negotiable."
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        {TARGETS.map((t, i) => (
          <motion.div
            key={t.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
          >
            <Link href={t.href} className="block h-full group">
              <div className={`relative glass-card rounded-2xl p-5 h-full overflow-hidden bg-gradient-to-br ${t.gradient} to-transparent border border-border/30 group-hover:scale-[1.01] transition-all`}>
                <div aria-hidden className="absolute -top-12 -right-12 size-40 rounded-full bg-current opacity-[0.04] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-md mb-3 group-hover:scale-110 group-hover:rotate-3 transition-all`}>
                    <t.icon className="size-5 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-2 ${t.accent}`}>{t.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed mb-3">{t.body}</p>
                  <div className={`inline-flex items-center gap-1.5 text-caption font-semibold ${t.accent} group-hover:gap-2.5 transition-all`}>
                    Explore <ArrowRight className="size-3" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}
